import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { Alert } from "@volca/ui/components/ui/alert";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { documentCategory } from "./document-categories";
import { ONBOARDING_DOCUMENT_TYPE } from "./types";

interface ReadyChecklistProps {
  hasPayPlan: boolean;
  hasReferenceData: boolean;
  error: string | null;
}

const CategoryState = ({ title, done }: { title: string; done: boolean }) => (
  <Stack direction="row" gap={2} align="center" className="shrink-0">
    {done ? (
      <CheckCircleIcon className="h-5 w-5 shrink-0 text-success" />
    ) : (
      <span
        aria-hidden="true"
        className="h-5 w-5 shrink-0 rounded-full border-2 border-line"
      />
    )}
    <Text tone={done ? "default" : "muted"}>{title}</Text>
  </Stack>
);

// What finished means, and where they stand against it. A strip under the page header
// rather than a card: it describes the step as a whole, so putting it between the drop
// pane and the action row made it read as another thing to do.
export const ReadyChecklist = ({
  hasPayPlan,
  hasReferenceData,
  error,
}: ReadyChecklistProps) => {
  const payPlanCategory = documentCategory(ONBOARDING_DOCUMENT_TYPE.payPlan);
  const referenceCategory = documentCategory(
    ONBOARDING_DOCUMENT_TYPE.referenceData,
  );

  return (
    <Stack className="w-full" gap={2} align="stretch">
      <Stack
        direction="row"
        gap={6}
        align="center"
        className="w-full flex-wrap"
      >
        <CategoryState title={payPlanCategory.title} done={hasPayPlan} />
        <CategoryState
          title={referenceCategory.title}
          done={hasReferenceData}
        />
        <Text
          variant="caption"
          tone="muted"
          // Its own line on a phone, rather than a sliver beside the two states.
          className="min-w-0 basis-full sm:flex-1"
        >
          {/* The instruction, not an alternative: there is no version of finished that
              leaves out the rules, so the copy never offers one. */}
          {hasPayPlan
            ? "Both kinds are in. Tell us you are finished when you have sent everything you have."
            : "Add your pay plan to finish. Without the rules written down we would be guessing at how your team is paid."}
        </Text>
      </Stack>
      {error && <Alert color="danger">{error}</Alert>}
    </Stack>
  );
};
